import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';

import configurations from '@/config';
import { getAccessToken } from '@/utils/api';

interface FetchExistingNotesRequest {
  currentPracticeId: string;
  currentOrganizationId: string;
  unifiedId: string;
}

interface FetchStandalonePatientVisitNotesRequest {
  currentPracticeId: string;
  currentOrganizationId: string;
  standalonePatientVisitId: string;
}

interface PatientConsent {
  createdAtISO: Date;
  dob: string | null;
  fullName: string;
  id: string;
}

export interface ClinicalNote {
  createdAt: Date;
  id: string;
  promptName: string;
  summary: string;
  editedSummary: string | null;
  unifiedAppointmentId?: string;
  standalonePatientVisitId?: string;
  updatedAt: Date;
  duration?: number;
  user?: {
    firstName: string;
    lastName: string;
  };

  writebackStatus?: VideaNoteWriteBackStatus;
  patientConsent: PatientConsent | null;
}

export interface VideaNoteWriteBackStatus {
  clinicalNoteId: string | null;
  isProcessed: boolean;
  processedAt: Date | null;
  errorCode: string | null;
  errorMessage: string | null;
  result: string | null;
}

const fetchExistingNotes = async ({
  currentPracticeId,
  currentOrganizationId,
  unifiedId,
}: FetchExistingNotesRequest) => {
  const { data } = await axios.get<ClinicalNote[]>(
    `${configurations.API_URL}/videa-notes/appointment-notes/${unifiedId}`,
    {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
        practice: currentPracticeId,
        org: currentOrganizationId,
      },
    },
  );
  return data;
};

const fetchStandalonePatientVisitNotes = async ({
  currentPracticeId,
  currentOrganizationId,
  standalonePatientVisitId,
}: FetchStandalonePatientVisitNotesRequest) => {
  const { data } = await axios.get<ClinicalNote[]>(
    `${configurations.API_URL}/videa-notes/standalone-patient-visit-notes/${standalonePatientVisitId}`,
    {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json',
        practice: currentPracticeId,
        org: currentOrganizationId,
      },
    },
  );
  return data;
};

export const useExistingNotes = (
  unifiedAppointmentId: string | undefined,
  enabled: boolean,
) => {
  const {
    primaryPracticeId: currentPracticeId,
    primaryOrganizationId: currentOrganizationId,
  } = useSelector((state: any) => state.login.user);

  return useQuery({
    queryKey: [
      'videa-notes',
      currentOrganizationId,
      currentPracticeId,
      unifiedAppointmentId,
    ],
    queryFn: () => {
      return fetchExistingNotes({
        currentPracticeId,
        currentOrganizationId,
        unifiedId: unifiedAppointmentId!,
      });
    },
    enabled: !!unifiedAppointmentId && enabled,
  });
};

export const useExistingStandalonePatientVisitNotes = (
  standalonePatientVisitId: string | undefined,
  enabled: boolean,
) => {
  const {
    primaryPracticeId: currentPracticeId,
    primaryOrganizationId: currentOrganizationId,
  } = useSelector((state: any) => state.login.user);

  return useQuery({
    queryKey: [
      'videa-notes',
      currentOrganizationId,
      currentPracticeId,
      standalonePatientVisitId,
    ],
    queryFn: () => {
      return fetchStandalonePatientVisitNotes({
        currentPracticeId,
        currentOrganizationId,
        standalonePatientVisitId: standalonePatientVisitId!,
      });
    },
    enabled: !!standalonePatientVisitId && enabled,
  });
};
